import { useState, useRef, useEffect } from 'react';
import { Button, Input, Spinner } from 'reactstrap';
import { GoogleGenerativeAI } from '@google/generative-ai';

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

/**
 * TutoredModeAIChat - Prep AI chat for tutored mode questions
 * Uses Gemini 2.0 Flash to help students understand questions
 * Restricts AI responses to only answer questions related to the current question
 */
const TutoredModeAIChat = ({
  questionText,
  explanationText,
  answers,
  correctAnswerId,
  userAnswerId,
  passageText,
  variant = 'standard' // 'standard' or 'pmr'
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  // Extract plain text from Draft.js format or HTML
  const extractPlainText = (content) => {
    if (!content) return '';

    // If it's a Draft.js raw content state object
    if (content.blocks && Array.isArray(content.blocks)) {
      return content.blocks.map(block => block.text).join('\n');
    }

    // If it's HTML string, strip tags
    if (typeof content === 'string') {
      return content.replace(/<[^>]*>/g, '').trim();
    }

    return String(content);
  };

  // Build context for the AI
  const buildContext = () => {
    const questionPlain = extractPlainText(questionText);
    const explanationPlain = extractPlainText(explanationText);
    const passagePlain = passageText ? extractPlainText(passageText) : null;

    const answersFormatted = answers?.map((a, idx) => {
      const letter = String.fromCharCode(65 + idx);
      const answerText = extractPlainText(a.answerText);
      const isCorrect = a.answerId === correctAnswerId;
      const isUserAnswer = a.answerId === userAnswerId;
      return `${letter}. ${answerText}${isCorrect ? ' (Correct Answer)' : ''}${isUserAnswer ? ' (User Selected)' : ''}`;
    }).join('\n') || '';

    return {
      question: questionPlain,
      explanation: explanationPlain,
      answers: answersFormatted,
      passage: passagePlain
    };
  };

  // Build the system prompt for the AI tutor
  const getSystemPrompt = () => {
    const context = buildContext();

    return `You are an AI tutor helping a student understand a medical exam question they just answered. Your role is to:

1. ONLY answer questions directly related to the current exam question, its explanation, the medical concepts involved, or related clinical/scientific knowledge
2. If the student asks something completely unrelated to medicine, healthcare, or the current question topic, politely redirect them back to the question

CURRENT QUESTION CONTEXT:
${context.passage ? `\n---PASSAGE---\n${context.passage}\n` : ''}
---QUESTION---
${context.question}

---ANSWER CHOICES---
${context.answers}

---EXPLANATION---
${context.explanation}

GUIDELINES:
- Be helpful, educational, and encouraging
- Explain concepts clearly at a medical student/resident level
- If asked about unrelated topics (like coding, recipes, general conversation, etc.), respond with: "I'm here to help you understand this medical question. Is there anything about the topic or the explanation you'd like me to clarify?"
- Keep responses concise but thorough
- Use medical terminology appropriately but explain complex terms when needed
- Reference the specific question context when relevant
- Format responses professionally with clean plain text. Do not use markdown symbols like asterisks, hashes, or underscores for formatting. Use numbered lists (1. 2. 3.) for steps and dashes for bullet points. Keep paragraphs short and well-structured.`;
  };

  // Call Gemini AI API
  const callGeminiAI = async (userMessage, conversationHistory) => {
    if (!GEMINI_API_KEY || GEMINI_API_KEY === 'YOUR_GEMINI_API_KEY_HERE') {
      throw new Error('Gemini API key is not configured. Please add REACT_APP_GEMINI_API_KEY to your .env file.');
    }

    const genAI = new GoogleGenerativeAI(GEMINI_API_KEY);
    const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });

    const systemPrompt = getSystemPrompt();

    // Build conversation history for Gemini
    const conversationText = conversationHistory.length > 0
      ? `\n\n---CONVERSATION HISTORY---\n${conversationHistory.map(msg =>
          `${msg.role === 'user' ? 'Student' : 'Tutor'}: ${msg.content}`
        ).join('\n')}`
      : '';

    const fullPrompt = `${systemPrompt}${conversationText}\n\nStudent: ${userMessage}\n\nTutor:`;

    const result = await model.generateContent(fullPrompt);
    const response = result.response;
    return response.text();
  };

  // Handle sending a message
  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');

    // Add user message to chat
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      const aiResponse = await callGeminiAI(userMessage, messages);
      setMessages([...newMessages, { role: 'assistant', content: aiResponse }]);
    } catch (error) {
      console.error('AI Chat Error:', error);
      setMessages([...newMessages, {
        role: 'assistant',
        content: error.message.includes('API key')
          ? 'Prep AI is not configured. Please contact support.'
          : 'I apologize, but I encountered an error. Please try again. If the problem persists, the AI service may be temporarily unavailable.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle key press (Enter to send)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Style configurations
  const isPmr = variant === 'pmr';
  const headerBg = isPmr ? '#1e6091' : '#5e72e4';
  const messageBubbleBg = isPmr ? '#e8f4f8' : '#f0f4ff';

  if (!isExpanded) {
    return (
      <div
        className={`mt-3 ${isPmr ? 'p-2' : 'p-3 border rounded'}`}
        style={isPmr ? {
          backgroundColor: '#f8f9fa',
          borderLeft: '3px solid #1e6091',
          fontSize: '0.85rem'
        } : {
          backgroundColor: '#f8f9fa'
        }}
      >
        <Button
          color={isPmr ? 'primary' : 'info'}
          size="sm"
          onClick={() => setIsExpanded(true)}
          style={isPmr ? {
            backgroundColor: '#1e6091',
            borderColor: '#1e6091',
            fontSize: '0.8rem'
          } : {}}
        >
          <i className="fas fa-robot mr-2"></i>
          Ask Prep AI About This Question
        </Button>
        <small className="text-muted d-block mt-1">
          Get help understanding the concepts in this question
        </small>
      </div>
    );
  }

  return (
    <div
      className={`mt-3 ${isPmr ? '' : 'border rounded'}`}
      style={{
        backgroundColor: '#ffffff',
        overflow: 'hidden',
        ...(isPmr ? { border: '1px solid #1e6091' } : {})
      }}
    >
      {/* Header */}
      <div
        className="d-flex justify-content-between align-items-center px-3 py-2"
        style={{ backgroundColor: headerBg }}
      >
        <span className="text-white font-weight-bold" style={{ fontSize: '0.85rem' }}>
          <i className="fas fa-robot mr-2"></i>
          Prep AI
        </span>
        <Button
          close
          className="text-white"
          onClick={() => setIsExpanded(false)}
          style={{ opacity: 1 }}
        />
      </div>

      {/* Messages Area */}
      <div
        className="p-3"
        style={{
          maxHeight: '300px',
          overflowY: 'auto',
          minHeight: '150px',
          backgroundColor: '#fafafa'
        }}
      >
        {messages.length === 0 ? (
          <div className="text-center text-muted py-4">
            <i className="fas fa-comments fa-2x mb-2" style={{ opacity: 0.5 }}></i>
            <p className="mb-0" style={{ fontSize: '0.85rem' }}>
              Ask any question about the concepts covered here.
            </p>
            <small>I can help explain the explanation, discuss related topics, or clarify confusing parts.</small>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div
              key={idx}
              className={`mb-2 d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
            >
              <div
                className="px-3 py-2 rounded"
                style={{
                  maxWidth: '85%',
                  backgroundColor: msg.role === 'user' ? headerBg : messageBubbleBg,
                  color: msg.role === 'user' ? '#ffffff' : '#333333',
                  fontSize: '0.85rem',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {msg.content}
              </div>
            </div>
          ))
        )}
        {isLoading && (
          <div className="d-flex justify-content-start mb-2">
            <div
              className="px-3 py-2 rounded"
              style={{
                backgroundColor: messageBubbleBg,
                fontSize: '0.85rem'
              }}
            >
              <Spinner size="sm" className="mr-2" />
              Thinking...
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-top p-2 d-flex align-items-center" style={{ backgroundColor: '#f5f5f5' }}>
        <Input
          type="text"
          placeholder="Ask about this question..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          innerRef={inputRef}
          style={{ fontSize: '0.85rem' }}
          className="mr-2"
        />
        <Button
          color={isPmr ? 'primary' : 'info'}
          size="sm"
          onClick={handleSend}
          disabled={!inputValue.trim() || isLoading}
          style={isPmr ? {
            backgroundColor: '#1e6091',
            borderColor: '#1e6091'
          } : {}}
        >
          <i className="fas fa-paper-plane"></i>
        </Button>
      </div>

      {/* Disclaimer */}
      <div className="px-3 py-1 border-top" style={{ backgroundColor: '#f9f9f9' }}>
        <small className="text-muted" style={{ fontSize: '0.7rem' }}>
          <i className="fas fa-info-circle mr-1"></i>
          AI responses are for learning purposes only. Always verify with authoritative sources.
        </small>
      </div>
    </div>
  );
};

export default TutoredModeAIChat;
